import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Sparkles, Calendar, Users, ExternalLink } from 'lucide-react';
import MediaCarousel from '../components/MediaCarousel';
import { absolutize } from '../components/MediaUrlsInput';
import { useLocalize } from '../i18n/useLocalize';
import PackageCTA from '../components/PackageCTA';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Home = () => {
  const [partnerPackages, setPartnerPackages] = useState([]);
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const localize = useLocalize();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pkgRes, destRes] = await Promise.all([
          axios.get(`${API}/partner-packages`),
          axios.get(`${API}/curated-destinations`)
        ]);
        setPartnerPackages(pkgRes.data.slice(0, 3));
        setDestinations(destRes.data.slice(0, 3));
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[600px] flex items-center overflow-hidden bg-ocean">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-25"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1663264396940-81050b8407b6?crop=entropy&cs=srgb&fm=jpg&q=85)',
          }}
        ></div>
        
        <div className="absolute inset-0 bg-gradient-to-r from-ocean/90 via-ocean/60 to-transparent"></div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <p className="text-terracotta font-semibold tracking-widest uppercase text-sm mb-4">{t('home.hero.kicker')}</p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-heading font-bold text-cream mb-6 leading-tight">
              {t('home.hero.title')}
            </h1>
            <p className="text-lg text-cream/85 mb-10 leading-relaxed max-w-lg">
              {t('home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link 
                to="/partners" 
                className="inline-flex items-center justify-center space-x-2 bg-terracotta text-white hover:bg-terracotta/90 hover:translate-y-[-2px] transition-all duration-200 font-bold tracking-wide uppercase px-8 py-4 rounded-sm shadow-lg"
                data-testid="explore-packages-btn"
              >
                <span>{t('home.hero.explorePackages')}</span>
                <ArrowRight size={20} />
              </Link>
              <Link 
                to="/consultation" 
                className="inline-flex items-center justify-center space-x-2 bg-white/15 backdrop-blur-sm text-cream border border-cream/30 hover:bg-white/25 hover:translate-y-[-2px] transition-all duration-200 font-bold tracking-wide uppercase px-8 py-4 rounded-sm"
                data-testid="book-consultation-btn"
              >
                <span>{t('home.hero.bookConsultation')}</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Destinations */}
      {destinations.length > 0 && (
        <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="destinations-section">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-3">
                <span>{t('home.destinations.title')}</span>
              </h2>
              <p className="text-base text-maroon/60">{t('home.destinations.subtitle')}</p>
            </div>
            <Link 
              to="/destinations" 
              className="hidden sm:inline-flex items-center space-x-2 text-terracotta hover:text-terracotta/80 font-bold"
              data-testid="view-all-destinations-link"
            >
              <span>{t('home.destinations.viewAll')}</span>
              <ArrowRight size={20} />
            </Link>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {destinations.map((rawDest, idx) => {
              const dest = localize(rawDest, ['title', 'vibe', 'description', 'facts']);
              const media = (dest.media && dest.media.length > 0) ? dest.media : (dest.image ? [dest.image] : []);
              return (
                <Link 
                  key={dest.id} 
                  to="/destinations"
                  className="card group cursor-pointer border hover:border-terracotta transition-colors"
                  data-testid={`home-destination-${idx}`}
                >
                  <MediaCarousel 
                    media={media}
                    alt={dest.title}
                  />
                  <h3 className="font-heading font-bold text-xl text-maroon mt-4 mb-2">{dest.title}</h3>
                  <p className="text-maroon/60 text-sm mb-3 line-clamp-2">{dest.vibe}</p>
                  <div className="flex items-center justify-between">
                    <div className="flex flex-wrap gap-1">
                      {(dest.facts || []).slice(0, 1).map((fact, i) => (
                        <span key={i} className="text-xs text-maroon/50">
                          {fact.label}: <span className="font-semibold text-maroon/70">{fact.value}</span>
                        </span>
                      ))}
                    </div>
                    <span className="text-terracotta font-semibold flex items-center space-x-1 text-sm">
                      <span>{t('home.destinations.explore')}</span>
                      <ArrowRight size={16} />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Mobile-only View All */}
          <div className="text-center mt-8 sm:hidden">
            <Link to="/destinations" className="inline-flex items-center space-x-2 text-terracotta font-bold">
              <span>{t('home.destinations.viewAll')}</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </section>
      )}

      {/* Travel Packages */}
      {partnerPackages.length > 0 && (
        <section className="py-16 bg-sand/40" data-testid="travel-packages-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-12">
              <div>
                <h2 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-3">
                  {t('home.travelPackages.title')}
                </h2>
                <p className="text-base text-maroon/60">{t('home.travelPackages.subtitle')}</p>
              </div>
              <Link
                to="/partners"
                className="hidden sm:inline-flex items-center space-x-2 text-terracotta hover:text-terracotta/80 font-bold"
                data-testid="view-all-packages-link"
              >
                <span>{t('home.travelPackages.viewAll')}</span>
                <ArrowRight size={20} />
              </Link>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {partnerPackages.map((rawPkg, idx) => {
                const pkg = localize(rawPkg, ['title', 'destination', 'duration', 'tags']);
                return (
                <div 
                  key={pkg.id} 
                  className="card group hover:border-terracotta/40 flex flex-col"
                  data-testid={`featured-package-${idx}`}
                >
                  {pkg.image && (
                    <div className="aspect-[4/3] overflow-hidden rounded-sm border border-maroon/10 mb-4">
                      <img
                        src={absolutize(pkg.image)}
                        alt={pkg.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                  )}
                  <p className="text-xs font-semibold text-terracotta uppercase tracking-wider mb-1">{pkg.supplier}</p>
                  <h3 className="font-heading font-bold text-2xl text-maroon mb-2">{pkg.title}</h3>
                  <p className="text-maroon/60 text-sm mb-4">{pkg.destination}</p>
                  <div className="mt-auto flex items-center justify-between pt-4 border-t border-maroon/10">
                    <div>
                      <p className="text-xs text-maroon/40">From</p>
                      <span className="text-2xl font-heading font-bold text-maroon">{pkg.price}</span>
                    </div>
                    <a
                      href={`/package-viewer?destination=${encodeURIComponent(pkg.supplier_url)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="hidden"
                    >
                      {t('home.travelPackages.viewDetails')}
                    </a>
                    <PackageCTA pkg={pkg} testIdPrefix="home-cta" />
                  </div>
                </div>
              );
              })}
            </div>
            
            <div className="text-center mt-12 sm:hidden">
              <Link to="/partners" className="inline-flex items-center space-x-2 text-terracotta font-bold" data-testid="view-all-packages-btn">
                <span>{t('home.travelPackages.viewAll')}</span>
                <ArrowRight size={18} />
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Why Choose Us */}
      <section className="py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold text-maroon mb-3">
            {t('home.whyChoose.title')}
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="text-center p-8 bg-terracotta/5 rounded-sm border border-terracotta/15 hover:border-terracotta/40 transition-colors">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-terracotta mb-4">
              <Sparkles size={32} className="text-white" />
            </div>
            <h3 className="font-heading font-bold text-xl text-maroon mb-3">{t('home.whyChoose.v1Title')}</h3>
            <p className="text-maroon/60">{t('home.whyChoose.v1Desc')}</p>
          </div>
          
          <div className="text-center p-8 bg-ocean/5 rounded-sm border border-ocean/15 hover:border-ocean/40 transition-colors">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-ocean mb-4">
              <Calendar size={32} className="text-white" />
            </div>
            <h3 className="font-heading font-bold text-xl text-maroon mb-3">{t('home.whyChoose.v2Title')}</h3>
            <p className="text-maroon/60">{t('home.whyChoose.v2Desc')}</p>
          </div>
          
          <div className="text-center p-8 bg-coral/5 rounded-sm border border-coral/15 hover:border-coral/40 transition-colors">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-coral mb-4">
              <Users size={32} className="text-white" />
            </div>
            <h3 className="font-heading font-bold text-xl text-maroon mb-3">{t('home.whyChoose.v3Title')}</h3>
            <p className="text-maroon/60">{t('home.whyChoose.v3Desc')}</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-ocean text-cream relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-40 h-40 bg-terracotta rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 left-20 w-56 h-56 bg-terracotta rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-4xl sm:text-5xl font-heading font-bold mb-6">
            {t('home.cta.title')}
          </h2>
          <p className="text-lg text-cream/80 mb-8">
            {t('home.cta.subtitle')}
          </p>
          <Link 
            to="/consultation" 
            className="inline-flex items-center space-x-2 bg-terracotta text-white hover:bg-terracotta/90 hover:translate-y-[-2px] transition-all duration-200 font-bold tracking-wide uppercase px-8 py-4 rounded-sm shadow-lg"
          >
            <span>{t('home.cta.button')}</span>
            <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
